// src/App.jsx
// Updated with subdomain-based routing for multi-tenant support
// Added: Google Analytics page view tracking
// Added: Authentication with Google and Microsoft OAuth
//
// Routing Logic:
// - invenire.app/* → Public/Super Admin routes
// - admin.invenire.app/* → Super Admin (redirects to /admin)
// - {tenant}.invenire.app/* → Client-specific routes
// - {tenant}.invenire.app/i/{token} → Interview for that tenant

import React, { useMemo, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { trackPageView } from "./utils/analytics";
import { AuthProvider } from "./contexts/AuthContext";
import { AriaLiveProvider } from "./components/AriaLiveAnnouncer";
import ProtectedRoute, { RequireAdmin, RequireSuperAdmin } from "./components/ProtectedRoute";

// Auth pages
import LoginPage from "./pages/LoginPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";

// Public pages
// eslint-disable-next-line no-unused-vars
import Landing from "./pages/Landing";
import InterviewPage from "./pages/InterviewPage";
import NotFoundPage from "./pages/NotFoundPage";
import BlueprintDebug from "./pages/BlueprintDebug";

// Invenire Admin pages (super admin - all clients)
import InvenireAdminDashboard from "./pages/admin/InvenireAdminDashboard";
import InvenireClientsPage from "./pages/admin/InvenireClientsPage";
import InvenireCandidatesPage from "./pages/admin/InvenireCandidatesPage";
import JobLoader from "./pages/admin/JobLoader";

// Super Admin Report pages
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import JobReportPage from "./pages/admin/JobReportPage";
import CandidateReportPage from "./pages/admin/CandidateReportPage";
import PausedInterviewsPage from "./pages/admin/PausedInterviewsPage";
import UserManagementPage from "./pages/admin/UserManagementPage";

// Client Admin pages (single client - branded)
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientConfigPage from "./pages/client/ClientConfigPage";
import ClientCandidatesPage from "./pages/client/ClientCandidatesPage";
import ClientJobsPage from "./pages/client/ClientJobsPage";

// Client Report pages
import ClientReportsPage from "./pages/client/ClientReportsPage";
import ClientJobReportPage from "./pages/client/ClientJobReportPage";
import ClientCandidateReportPage from "./pages/client/ClientCandidateReportPage";
import ClientUserManagementPage from "./pages/client/ClientUserManagementPage";

// =============================================================================
// CONFIGURATION
// =============================================================================

// Set to false to disable auth during development
const REQUIRE_AUTH = process.env.REACT_APP_REQUIRE_AUTH !== 'false';

// =============================================================================
// PAGE VIEW TRACKING
// =============================================================================

/**
 * Component that tracks page views on route changes
 * Must be inside BrowserRouter to use useLocation
 */
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    // Mask interview tokens for privacy (don't send full token to GA)
    let path = location.pathname;
    if (path.includes('/i/')) {
      // Replace token with placeholder for privacy
      path = path.replace(/\/i\/[^/]+/, '/i/[token]');
    }
    if (path.includes('/interviews/')) {
      // Replace interview ID with placeholder
      path = path.replace(/\/interviews\/\d+/, '/interviews/[id]');
    }
    
    trackPageView(path + location.search, document.title);
  }, [location]);
  
  return null;
}

// =============================================================================
// SUBDOMAIN DETECTION
// =============================================================================

/**
 * Parse the current hostname to detect tenant subdomain
 * 
 * Examples:
 *   invenire.app → { isRoot: true, subdomain: null }
 *   www.invenire.app → { isRoot: true, subdomain: null }
 *   admin.invenire.app → { isRoot: true, subdomain: null, isAdmin: true }
 *   supabase.invenire.app → { isRoot: false, subdomain: "supabase" }
 *   localhost:3000 → { isRoot: true, subdomain: null } (dev mode)
 */
function parseSubdomain() {
  const hostname = window.location.hostname;
  
  // Development mode - check for subdomain simulation via query param
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const params = new URLSearchParams(window.location.search);
    const simulatedTenant = params.get("tenant");
    const simulatedAdmin = params.get("admin") === "true";
    if (simulatedTenant) {
      return {
        isRoot: false,
        subdomain: simulatedTenant,
        isAdmin: false,
        isDev: true
      };
    }
    if (simulatedAdmin) {
      return { isRoot: true, subdomain: null, isAdmin: true, isDev: true };
    }
    return { isRoot: true, subdomain: null, isAdmin: false, isDev: true };
  }
  
  // Production - parse actual subdomain
  // Expected format: {subdomain}.invenire.app
  const parts = hostname.split(".");
  
  // Handle different domain patterns
  // invenire.app (2 parts)
  // www.invenire.app (3 parts, www)
  // supabase.invenire.app (3 parts, tenant)
  // admin.invenire.app (3 parts, admin)
  
  if (parts.length <= 2) {
    // No subdomain: invenire.app
    return { isRoot: true, subdomain: null, isAdmin: false, isDev: false };
  }
  
  const subdomain = parts[0].toLowerCase();
  
  // Reserved subdomains that should act as root
  const reservedSubdomains = ["www", "api", "app"];
  if (reservedSubdomains.includes(subdomain)) {
    return { isRoot: true, subdomain: null, isAdmin: false, isDev: false };
  }
  
  // Admin subdomain - super admin access
  if (subdomain === "admin") {
    return { isRoot: true, subdomain: null, isAdmin: true, isDev: false };
  }
  
  // Tenant subdomain
  return { isRoot: false, subdomain: subdomain, isAdmin: false, isDev: false };
}

// =============================================================================
// CONDITIONAL AUTH WRAPPER
// =============================================================================

/**
 * Wrapper that conditionally requires authentication
 * Can be disabled via REACT_APP_REQUIRE_AUTH=false for development
 */
function MaybeProtected({ children, requireAdmin, requireSuperAdmin }) {
  if (!REQUIRE_AUTH) {
    return children;
  }
  
  if (requireSuperAdmin) {
    return <RequireSuperAdmin>{children}</RequireSuperAdmin>;
  }
  
  if (requireAdmin) {
    return <RequireAdmin>{children}</RequireAdmin>;
  }
  
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

// =============================================================================
// ROUTE COMPONENTS
// =============================================================================

/**
 * Root/Super Admin Routes
 * Shown on invenire.app and www.invenire.app
 */
function RootRoutes() {
  return (
    <Routes>
      {/* Root redirects to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Public - No auth required */}
      <Route path="/i/:token" element={<InterviewPage />} />
      <Route path="/debug/blueprint" element={<BlueprintDebug />} />
      
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Super Admin - Requires super_admin role */}
      <Route path="/admin" element={
        <MaybeProtected requireSuperAdmin>
          <InvenireAdminDashboard />
        </MaybeProtected>
      } />
      <Route path="/admin/clients" element={
        <MaybeProtected requireSuperAdmin>
          <InvenireClientsPage />
        </MaybeProtected>
      } />
      <Route path="/admin/candidates" element={
        <MaybeProtected requireSuperAdmin>
          <InvenireCandidatesPage />
        </MaybeProtected>
      } />
      <Route path="/admin/jobs" element={
        <MaybeProtected requireSuperAdmin>
          <JobLoader />
        </MaybeProtected>
      } />
      
      {/* Super Admin Reports */}
      <Route path="/admin/reports" element={
        <MaybeProtected requireSuperAdmin>
          <AdminReportsPage />
        </MaybeProtected>
      } />
      <Route path="/admin/jobs/:jobId/report" element={
        <MaybeProtected requireSuperAdmin>
          <JobReportPage />
        </MaybeProtected>
      } />
      <Route path="/admin/interviews/:interviewId/report" element={
        <MaybeProtected requireSuperAdmin>
          <CandidateReportPage />
        </MaybeProtected>
      } />
      <Route path="/admin/paused" element={
        <MaybeProtected requireSuperAdmin>
          <PausedInterviewsPage />
        </MaybeProtected>
      } />
      <Route path="/admin/users" element={
        <MaybeProtected requireSuperAdmin>
          <UserManagementPage />
        </MaybeProtected>
      } />
      
      {/* Client admin via path - Requires auth */}
      <Route path="/client/:tenantKey" element={
        <MaybeProtected>
          <ClientDashboard />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/config" element={
        <MaybeProtected requireAdmin>
          <ClientConfigPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/candidates" element={
        <MaybeProtected>
          <ClientCandidatesPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/jobs" element={
        <MaybeProtected>
          <ClientJobsPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/reports" element={
        <MaybeProtected>
          <ClientReportsPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/jobs/:jobId/report" element={
        <MaybeProtected>
          <ClientJobReportPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/interviews/:interviewId/report" element={
        <MaybeProtected>
          <ClientCandidateReportPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/users" element={
        <MaybeProtected requireAdmin>
          <ClientUserManagementPage />
        </MaybeProtected>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Admin Subdomain Routes
 * Shown on admin.invenire.app
 * Root redirects to /admin dashboard
 */
function AdminRoutes() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Root of admin subdomain → redirect to dashboard */}
      <Route path="/" element={<Navigate to="/admin" replace />} />
      
      {/* Super Admin pages - Requires super_admin role */}
      <Route path="/admin" element={
        <MaybeProtected requireSuperAdmin>
          <InvenireAdminDashboard />
        </MaybeProtected>
      } />
      <Route path="/admin/clients" element={
        <MaybeProtected requireSuperAdmin>
          <InvenireClientsPage />
        </MaybeProtected>
      } />
      <Route path="/admin/candidates" element={
        <MaybeProtected requireSuperAdmin>
          <InvenireCandidatesPage />
        </MaybeProtected>
      } />
      <Route path="/admin/jobs" element={
        <MaybeProtected requireSuperAdmin>
          <JobLoader />
        </MaybeProtected>
      } />
      
      {/* Super Admin Reports */}
      <Route path="/admin/reports" element={
        <MaybeProtected requireSuperAdmin>
          <AdminReportsPage />
        </MaybeProtected>
      } />
      <Route path="/admin/jobs/:jobId/report" element={
        <MaybeProtected requireSuperAdmin>
          <JobReportPage />
        </MaybeProtected>
      } />
      <Route path="/admin/interviews/:interviewId/report" element={
        <MaybeProtected requireSuperAdmin>
          <CandidateReportPage />
        </MaybeProtected>
      } />
      <Route path="/admin/paused" element={
        <MaybeProtected requireSuperAdmin>
          <PausedInterviewsPage />
        </MaybeProtected>
      } />
      <Route path="/admin/users" element={
        <MaybeProtected requireSuperAdmin>
          <UserManagementPage />
        </MaybeProtected>
      } />
      
      {/* Client admin via path */}
      <Route path="/client/:tenantKey" element={
        <MaybeProtected>
          <ClientDashboard />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/config" element={
        <MaybeProtected requireAdmin>
          <ClientConfigPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/candidates" element={
        <MaybeProtected>
          <ClientCandidatesPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/jobs" element={
        <MaybeProtected>
          <ClientJobsPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/reports" element={
        <MaybeProtected>
          <ClientReportsPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/jobs/:jobId/report" element={
        <MaybeProtected>
          <ClientJobReportPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/interviews/:interviewId/report" element={
        <MaybeProtected>
          <ClientCandidateReportPage />
        </MaybeProtected>
      } />
      <Route path="/client/:tenantKey/users" element={
        <MaybeProtected requireAdmin>
          <ClientUserManagementPage />
        </MaybeProtected>
      } />
      
      {/* Interviews can still be accessed (no auth - for candidates) */}
      <Route path="/i/:token" element={<InterviewPage />} />
      
      {/* Debug */}
      <Route path="/debug/blueprint" element={<BlueprintDebug />} />
      
      {/* Fallback */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Tenant-Specific Routes
 * Shown on {tenant}.invenire.app
 * 
 * The tenantKey is passed via context, not URL params
 * Fetches tenant settings to check if auth is required
 */
function TenantRoutes({ tenantKey }) {
  const [tenantSettings, setTenantSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    async function loadTenantSettings() {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.invenire.app';
        const res = await fetch(`${API_BASE}/tenants/${tenantKey}`);
        if (res.ok) {
          const data = await res.json();
          setTenantSettings(data);
        }
      } catch (err) {
        console.error("[Invenire] Failed to load tenant settings:", err);
      } finally {
        setLoading(false);
      }
    }
    
    if (tenantKey) {
      loadTenantSettings();
    } else {
      setLoading(false);
    }
  }, [tenantKey]);
  
  // Check if auth is required for this tenant
  const requireAuth = tenantSettings?.require_auth !== false;
  
  // Log for debugging
  useEffect(() => {
    if (!loading) {
      console.log("[Invenire] Tenant auth settings:", {
        tenantKey,
        requireAuth,
        tenantSettings
      });
    }
  }, [loading, tenantKey, requireAuth, tenantSettings]);
  
  // Wrapper that respects tenant's auth setting
  function TenantProtected({ children, requireAdmin }) {
    if (!requireAuth) {
      return children;
    }
    return (
      <MaybeProtected requireAdmin={requireAdmin}>
        {children}
      </MaybeProtected>
    );
  }
  
  // Show loading while fetching tenant settings
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#0a0a0a',
        color: '#888'
      }}>
        Loading...
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Interview - main use case for candidates (no auth required) */}
      <Route path="/i/:token" element={<InterviewPage tenantKey={tenantKey} />} />
      
      {/* Tenant landing (could show branded landing or redirect to admin) */}
      <Route path="/" element={<Navigate to="/admin" replace />} />
      
      {/* Client Admin - auth depends on tenant settings */}
      <Route path="/admin" element={
        <TenantProtected>
          <ClientDashboard tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      <Route path="/admin/config" element={
        <TenantProtected requireAdmin>
          <ClientConfigPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      <Route path="/admin/candidates" element={
        <TenantProtected>
          <ClientCandidatesPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      <Route path="/admin/jobs" element={
        <TenantProtected>
          <ClientJobsPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      
      {/* Client Reports */}
      <Route path="/admin/reports" element={
        <TenantProtected>
          <ClientReportsPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      <Route path="/admin/jobs/:jobId/report" element={
        <TenantProtected>
          <ClientJobReportPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      <Route path="/admin/interviews/:interviewId/report" element={
        <TenantProtected>
          <ClientCandidateReportPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      <Route path="/admin/users" element={
        <TenantProtected requireAdmin>
          <ClientUserManagementPage tenantKeyFromSubdomain={tenantKey} />
        </TenantProtected>
      } />
      
      {/* Also support /client paths for backwards compatibility */}
      <Route path="/client" element={<Navigate to="/admin" replace />} />
      <Route path="/client/config" element={<Navigate to="/admin/config" replace />} />
      <Route path="/client/candidates" element={<Navigate to="/admin/candidates" replace />} />
      <Route path="/client/jobs" element={<Navigate to="/admin/jobs" replace />} />
      <Route path="/client/reports" element={<Navigate to="/admin/reports" replace />} />
      <Route path="/client/users" element={<Navigate to="/admin/users" replace />} />
      
      {/* Debug tools */}
      <Route path="/debug/blueprint" element={<BlueprintDebug tenantKey={tenantKey} />} />
      
      {/* Fallback */}
      <Route path="*" element={<NotFoundPage tenantKey={tenantKey} />} />
    </Routes>
  );
}

// =============================================================================
// MAIN APP
// =============================================================================

export default function App() {
  // Parse subdomain once on app load
  const subdomainInfo = useMemo(() => parseSubdomain(), []);
  
  // Log for debugging (remove in production)
  console.log("[Invenire] Subdomain detection:", subdomainInfo);
  console.log("[Invenire] Current hostname:", window.location.hostname);
  console.log("[Invenire] Current pathname:", window.location.pathname);
  console.log("[Invenire] Auth required:", REQUIRE_AUTH);
  
  return (
    <BrowserRouter>
      <AuthProvider>
        <AriaLiveProvider>
          {/* Track page views on route changes */}
          <PageViewTracker />
          
          {subdomainInfo.isAdmin ? (
            // admin.invenire.app → Admin routes (root redirects to /admin)
            <AdminRoutes />
          ) : subdomainInfo.isRoot ? (
            // Root domain (invenire.app) → Public + Super Admin routes
            <RootRoutes />
          ) : (
            // Tenant subdomain → Client-specific routes
            <TenantRoutes tenantKey={subdomainInfo.subdomain} />
          )}
        </AriaLiveProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// =============================================================================
// EXPORTS FOR USE IN OTHER COMPONENTS
// =============================================================================

/**
 * Hook to get current tenant context
 * Can be used in any component to know which tenant we're on
 */
export function useTenantContext() {
  return useMemo(() => parseSubdomain(), []);
}

/**
 * Get current tenant key (if any)
 * Returns null if on root domain
 */
export function getCurrentTenantKey() {
  const info = parseSubdomain();
  return info.subdomain;
}
